import { Currency, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import ERC20_ABI from 'abis/erc20.json'
import { useConnection } from 'hooks/useConnection'
import { useTokenAllowance } from 'hooks/useTokenAllowance'
import { useWriteContract } from 'hooks/useTransaction'
import { useCallback, useMemo } from 'react'
import { useApprovalCache } from 'state/approvalCache/hooks'
import type { Abi, Address } from 'viem'
import { Hex, maxUint256 } from 'viem'

export enum ApprovalState {
  UNKNOWN = 'UNKNOWN',
  NOT_APPROVED = 'NOT_APPROVED',
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
}

export function useApprovalStateForSpender(
  amountToApprove: CurrencyAmount<Currency> | undefined,
  spender: string | undefined,
  useIsPendingApproval: (token?: Token, spender?: string) => boolean
): ApprovalState {
  const { account } = useConnection()
  const token = amountToApprove?.currency?.isToken ? amountToApprove.currency : undefined

  const currentAllowance = useTokenAllowance(token, account ?? undefined, spender)
  const pendingApproval = useIsPendingApproval(token, spender)
  const [approvalCache] = useApprovalCache(
    amountToApprove?.currency?.isToken ? amountToApprove.currency.wrapped : undefined,
    spender
  )
  return useMemo(() => {
    if (!amountToApprove || !spender) return ApprovalState.UNKNOWN
    if (amountToApprove.currency.isNative) return ApprovalState.APPROVED
    if (!token) return ApprovalState.UNKNOWN

    if (!currentAllowance) return ApprovalState.UNKNOWN
    // we might not have enough data to know whether or not we need to approve
    if (approvalCache && !currentAllowance.lessThan(amountToApprove)) return ApprovalState.APPROVED

    if (currentAllowance.lessThan(amountToApprove)) {
      // Check if approval is pending
      return pendingApproval ? ApprovalState.PENDING : ApprovalState.NOT_APPROVED
    } else {
      // We have sufficient allowance
      return ApprovalState.APPROVED
    }
  }, [amountToApprove, spender, token, currentAllowance, approvalCache, pendingApproval])
}

export function useApproval(
  amountToApprove: CurrencyAmount<Currency> | undefined,
  spender: string | undefined,
  useIsPendingApproval: (token?: Token, spender?: string) => boolean,
  useExactApproval = false
): [
  ApprovalState,
  () => Promise<{ response: { hash: Hex }; tokenAddress: string; spenderAddress: string } | undefined>
] {
  const { chainId, wagmiPublicClient, account } = useConnection()
  const token = amountToApprove?.currency?.isToken ? amountToApprove.currency : undefined
  const [, confirmApprovalCache] = useApprovalCache(amountToApprove?.currency.wrapped, spender)

  // check the current approval status
  const approvalState = useApprovalStateForSpender(amountToApprove, spender, useIsPendingApproval)

  const { writeContractAsync } = useWriteContract()

  const approve = useCallback(async () => {
    function logFailure(error: Error | string): undefined {
      console.warn(`${token?.symbol || 'Token'} approval failed:`, error)
      return
    }

    // Bail early if there is an issue.
    if (approvalState !== ApprovalState.NOT_APPROVED) {
      return logFailure('approve was called unnecessarily')
    } else if (!chainId) {
      return logFailure('no chainId')
    } else if (!token) {
      return logFailure('no token')
    } else if (!amountToApprove) {
      return logFailure('missing amount to approve')
    } else if (!spender) {
      return logFailure('no spender')
    } else if (!wagmiPublicClient) {
      return logFailure('no wagmi public client')
    }

    const approvalAmount = useExactApproval ? BigInt(amountToApprove.quotient.toString()) : maxUint256

    try {
      // Simulate the contract call first
      const { request } = await wagmiPublicClient.simulateContract({
        address: token.address as Address,
        abi: ERC20_ABI as Abi,
        functionName: 'approve',
        args: [spender as Address, approvalAmount],
        account: account ?? undefined,
      })

      // Send the transaction using the simulation result
      const hash = await writeContractAsync(request)

      // Confirm approval cache after transaction is submitted
      confirmApprovalCache()

      return {
        response: { hash },
        tokenAddress: token.address,
        spenderAddress: spender,
      }
    } catch (error) {
      logFailure(error as Error)
      return undefined
    }
  }, [
    approvalState,
    token,
    amountToApprove,
    spender,
    chainId,
    confirmApprovalCache,
    writeContractAsync,
    wagmiPublicClient,
    account,
    useExactApproval,
  ])

  return [approvalState, approve]
}
